import { useEffect, useState } from 'react';
import { getPatientQuickSelections } from 'services/api';

export type QuickSelectionType = 'PATHOLOGY' | 'INTERVENTION' | 'ALLERGY' | 'MEDICATION' | 'VACCINE' | 'FCS_SITUATION';

export type QuickSelectionOption = {
  code?: string;
  label: string;
  value: string;
};

/**
 * Lista de selección rápida de un formulario del paciente, configurada por grupo en gdp-api (la del grupo del
 * paciente más la de sus ancestros, ya ordenada). Se carga al abrir el formulario y vacía si falla o no hay:
 * en ese caso el formulario no pinta el panel.
 *
 * Elegir un elemento copia `value` (y `code`) en el formulario; no hay enlace con la lista.
 *
 * `isEmpty` solo es true cuando la carga ha terminado bien y el grupo no tiene lista: mientras carga o si falla es
 * false, para no ofrecer «configurar» a un grupo que sí la tiene.
 */
export const usePatientQuickSelections = (
  patientId: string | undefined,
  type: QuickSelectionType,
  open: boolean,
): { items: QuickSelectionOption[]; isEmpty: boolean } => {
  const [items, setItems] = useState<QuickSelectionOption[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;
    setLoaded(false);
    getPatientQuickSelections(patientId, type)
      .then((response: any) => {
        if (cancelled) return;
        const ok = !!response?.data?.success;
        const list = ok ? response.data.item ?? [] : [];
        setItems(list.map((item: any) => ({ code: item.code ?? undefined, label: item.label, value: item.label })));
        setLoaded(ok);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, patientId, type]);

  return { items, isEmpty: loaded && items.length === 0 };
};
